import React, { useState, useEffect } from 'react';
import { X, Calendar, Clock, MapPin, Send } from 'lucide-react';
import { api } from '../services/api';

const CalendarInviteModal = ({ open, onClose, contact }) => {
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  // Form state
  const [title, setTitle] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('10:00');
  const [duration, setDuration] = useState(60);
  const [location, setLocation] = useState('');
  const [description, setDescription] = useState('');

  // Set default date to tomorrow
  useEffect(() => {
    if (open) {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      setDate(tomorrow.toISOString().split('T')[0]);
      setTitle(`Test Drive - ${contact?.first_name || 'Customer'}`);
      setSuccess(false);
      setError(null);
    }
  }, [open, contact]);

  const handleSend = async () => {
    if (!contact?.id) return;
    
    setIsSending(true);
    setError(null);
    
    try {
      // Combine date and time into ISO datetime
      const startTime = new Date(`${date}T${time}`);
      const endTime = new Date(startTime.getTime() + duration * 60000);
      
      await api.sendCalendarInvite(contact.id, {
        title,
        start_time: startTime.toISOString(),
        end_time: endTime.toISOString(),
        location,
        description,
      });
      
      setSuccess(true);
      setTimeout(() => {
        onClose?.({ sent: true });
      }, 1500);
    } catch (e) {
      setError(e?.message || 'Failed to send calendar invite');
    } finally {
      setIsSending(false);
    }
  };

  const isValid = title.trim() && date && time && duration > 0;

  if (!open) return null;

  return (
    <div style={styles.overlay} onClick={() => onClose?.({ sent: false })}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <div>
            <div style={styles.title}>Send Calendar Invite</div>
            <div style={styles.subtitle}>
              To: <span style={{ color: '#e2e8f0' }}>{contact?.email || '(no email)'}</span>
            </div>
          </div>
          <button style={styles.iconBtn} onClick={() => onClose?.({ sent: false })}>
            <X size={18} />
          </button>
        </div>

        {error && (
          <div style={styles.error}>
            {error}
          </div>
        )}

        {success && (
          <div style={styles.success}>
            Calendar invite sent successfully!
          </div>
        )}

        <div style={styles.form}>
          <label style={styles.label}>Event Title</label>
          <input
            style={styles.input}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g., Test Drive - John"
            disabled={isSending || success}
          />

          <div style={styles.row}>
            <div style={styles.col}>
              <label style={styles.label}>Date</label>
              <input
                type="date"
                style={styles.input}
                value={date}
                onChange={(e) => setDate(e.target.value)}
                disabled={isSending || success}
              />
            </div>
            <div style={styles.col}>
              <label style={styles.label}>Time</label>
              <input
                type="time"
                style={styles.input}
                value={time}
                onChange={(e) => setTime(e.target.value)}
                disabled={isSending || success}
              />
            </div>
          </div>

          <label style={styles.label}>Duration</label>
          <select
            style={styles.input}
            value={duration}
            onChange={(e) => setDuration(Number(e.target.value))}
            disabled={isSending || success}
          >
            <option value={15}>15 minutes</option>
            <option value={30}>30 minutes</option>
            <option value={45}>45 minutes</option>
            <option value={60}>1 hour</option>
            <option value={90}>1.5 hours</option>
            <option value={120}>2 hours</option>
          </select>

          <label style={styles.label}>Location</label>
          <input
            style={styles.input}
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="e.g., 123 Main St, Dealership"
            disabled={isSending || success}
          />

          <label style={styles.label}>Description (optional)</label>
          <textarea
            style={styles.textarea}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Additional details about the appointment..."
            rows={3}
            disabled={isSending || success}
          />
        </div>

        <div style={styles.footer}>
          <button
            style={{ ...styles.primaryBtn, opacity: (!isValid || isSending || success) ? 0.6 : 1 }}
            onClick={handleSend}
            disabled={!isValid || isSending || success || !contact?.email}
          >
            <Send size={16} />
            {isSending ? 'Sending…' : success ? 'Sent!' : 'Send Invite'}
          </button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000,
    padding: 16,
  },
  modal: {
    width: '100%',
    maxWidth: 480,
    maxHeight: '90vh',
    backgroundColor: '#0f172a',
    border: '1px solid #334155',
    borderRadius: 14,
    boxShadow: '0 20px 80px rgba(0,0,0,0.45)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    padding: 16,
    borderBottom: '1px solid #1e293b',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 10,
  },
  title: { color: '#f1f5f9', fontSize: 16, fontWeight: 700 },
  subtitle: { color: '#94a3b8', fontSize: 13, marginTop: 4 },
  iconBtn: {
    background: 'transparent',
    border: '1px solid #334155',
    borderRadius: 10,
    color: '#cbd5e1',
    cursor: 'pointer',
    padding: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  error: {
    padding: '10px 16px',
    background: 'rgba(239, 68, 68, 0.12)',
    borderBottom: '1px solid rgba(239, 68, 68, 0.3)',
    color: '#fecaca',
    fontSize: 13,
  },
  success: {
    padding: '10px 16px',
    background: 'rgba(34, 197, 94, 0.12)',
    borderBottom: '1px solid rgba(34, 197, 94, 0.3)',
    color: '#86efac',
    fontSize: 13,
  },
  form: { padding: 16, display: 'flex', flexDirection: 'column', gap: 8, overflow: 'auto' },
  label: { color: '#94a3b8', fontSize: 12, fontWeight: 700, marginTop: 8 },
  input: {
    width: '100%',
    padding: '10px 12px',
    backgroundColor: '#0b1222',
    border: '1px solid #334155',
    borderRadius: 10,
    color: '#e2e8f0',
    fontSize: 14,
  },
  textarea: {
    width: '100%',
    padding: '10px 12px',
    backgroundColor: '#0b1222',
    border: '1px solid #334155',
    borderRadius: 10,
    color: '#e2e8f0',
    fontSize: 14,
    resize: 'vertical',
    fontFamily: 'inherit',
  },
  row: {
    display: 'flex',
    gap: 12,
  },
  col: {
    flex: 1,
  },
  footer: {
    padding: '12px 16px',
    borderTop: '1px solid #1e293b',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 10,
  },
  primaryBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 16px',
    borderRadius: 10,
    border: 'none',
    background: 'var(--brand-primary, #3b82f6)',
    color: '#fff',
    fontWeight: 600,
    cursor: 'pointer',
  },
};

export default CalendarInviteModal;
